import React, { FC, useCallback, useEffect, useState } from "react";
import { styled } from "@mui/system";
import { GAPS } from "../../common/consts";
import Text, { TextVariant } from "../Display/Text";
import Button, { ButtonSizes, ButtonVariants } from "../Display/Button";
import { listMcpConnections, revokeMcpConnection } from "../../services/firebase";
import { McpConnection, McpConnections } from "../../types/mcp";
import { isProduction } from "../../services/config";

/** The address users add as a custom connector (docs/MCP.md). */
export const MCP_CONNECTOR_URL = isProduction ? "https://mcp.boringplugins.com/mcp" : "https://europe-west3-higene-app.cloudfunctions.net/mcp/mcp";

const Column = styled("div")`
  display: flex;
  flex-direction: column;
  gap: ${GAPS.md};
`;

const Item = styled("div")`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${GAPS.md};
`;

const Muted = styled(Text)`
  opacity: 0.7;
`;

const when = (ms: number | null) => (ms ? new Date(ms).toLocaleDateString() : "never");

/**
 * Settings > Connected AI apps: the AI apps (Claude, ChatGPT) connected to
 * boring through its MCP connector. Disconnecting ends the app's access at
 * once. Admins also see and can disconnect everyone's in the organization.
 */
const ConnectedAiApps: FC = () => {
  const [data, setData] = useState<McpConnections | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [revoking, setRevoking] = useState<string | null>(null);

  const load = useCallback(() => {
    listMcpConnections()
      .then(setData)
      .catch((e) => setError(e?.message || "Could not load connected AI apps"));
  }, []);

  useEffect(load, [load]);

  const revoke = async (c: McpConnection) => {
    if (!window.confirm(`Disconnect ${c.clientName}${c.userEmail ? ` for ${c.userEmail}` : ""}? It loses access to boring right away.`)) return;
    setRevoking(c.id);
    try {
      await revokeMcpConnection(c.id);
      load();
    } catch (e: any) {
      setError(e?.message || "Could not disconnect");
    } finally {
      setRevoking(null);
    }
  };

  const row = (c: McpConnection, showUser: boolean) => (
    <Item key={c.id}>
      <div>
        <Text variant={TextVariant.md}>{c.clientName}</Text>
        <Muted variant={TextVariant.sm}>
          {showUser && c.userEmail ? `${c.userEmail} · ` : ""}
          {c.canWrite ? "Can make changes" : "Read only"} · returns to {c.redirectHost} · connected {when(c.createdAt)} · last used {when(c.lastUsedAt)}
        </Muted>
      </div>
      <Button variant={ButtonVariants.light} size={ButtonSizes.sm} onClick={() => revoke(c)} loading={revoking === c.id} disabled={!!revoking}>
        Disconnect
      </Button>
    </Item>
  );

  if (error) return <Text variant={TextVariant.sm}>{error}</Text>;
  if (!data) return <Muted variant={TextVariant.sm}>Loading...</Muted>;
  const others = (data.organization || []).filter((c) => !data.mine.some((m) => m.id === c.id));
  return (
    <Column>
      <Muted variant={TextVariant.sm}>
        AI apps like Claude and ChatGPT can use boring through its connector: add {MCP_CONNECTOR_URL} as a custom connector in the app, and sign in here when it asks.
      </Muted>
      {data.mine.length ? data.mine.map((c) => row(c, false)) : <Text variant={TextVariant.sm}>You have not connected any AI apps.</Text>}
      {data.isAdmin && others.length > 0 && (
        <>
          <Text variant={TextVariant.md}>Others in your organization</Text>
          {others.map((c) => row(c, true))}
        </>
      )}
    </Column>
  );
};

export default ConnectedAiApps;
